import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { fetchArchdeaconries } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { MapPin, User, Church, ChevronRight, Info, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import SeoHead from '@/components/Seo';
import { motion } from 'framer-motion';

interface Archdeaconry {
  id: number;
  name: string;
  slug: string;
  description?: string | null;
  parishes?: { id: number; name: string }[];
  current_archdeacon?: {
    id: number;
    title: string;
    name: string;
    photo?: string;
  };
  current_supervisor?: {
    id: number;
    title: string;
    name: string;
    photo?: string;
  };
}

const Archdeaconries = () => {
  const [archdeaconries, setArchdeaconries] = useState<Archdeaconry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadArchdeaconries = async () => {
      try {
        const response = await fetchArchdeaconries();
        setArchdeaconries(response.data);
      } catch (error) {
        console.error('Error fetching archdeaconries:', error);
        setArchdeaconries([]);
      } finally {
        setLoading(false);
      }
    };
    loadArchdeaconries();
  }, []);

  // --- SEO BLOCK ---
  const seoContent = (
    <SeoHead
      title="Diocesan Archdeaconries | Ibadan South Diocese"
      description="Exploring the administrative heart of our diocese. View the various archdeaconries and the leadership overseeing our spiritual growth and mission."
      ogImage="https://res.cloudinary.com/dwrqyrm84/image/upload/v1750874183/archdeaconry_og.jpg"
    />
  );

  if (loading) return (
    <>
      {seoContent}
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#fcfaf7]">
        <div className="h-12 w-12 border-4 border-amber-600/10 border-t-amber-600 rounded-full animate-spin mb-4" />
        <div className="animate-pulse font-serif italic text-slate-400">Organizing Diocesan Records...</div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#fcfaf7] py-24">
      {seoContent}
      
      <div className="max-w-7xl mx-auto px-6">
        {/* Elegant Header */}
        <div className="text-center mb-20">
          <motion.div 
            initial={{ opacity: 0, y: -10 }} 
            animate={{ opacity: 1, y: 0 }} 
            className="inline-block px-4 py-1.5 bg-slate-900 text-white text-[10px] font-black uppercase tracking-[0.4em] rounded-full mb-6"
          >
            Administrative Hierarchy
          </motion.div>
          <h1 className="text-5xl lg:text-7xl font-serif font-bold text-slate-900 mb-6">
            Diocesan <span className="text-amber-600">Archdeaconries</span>
          </h1>
          <p className="text-lg text-slate-500 max-w-3xl mx-auto font-serif italic leading-relaxed">
            The administrative pillars of the Diocese of Ibadan South, facilitating mission
            and ministry across all pastoral areas.
          </p>
        </div>

        {/* Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {archdeaconries.map((arch, index) => {
            const leader = arch.current_archdeacon || arch.current_supervisor;
            const isSupervisor = !arch.current_archdeacon && arch.current_supervisor;

            return (
              <motion.div
                key={arch.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="group relative overflow-hidden border-none transition-all duration-700 rounded-[2.5rem] shadow-sm hover:shadow-2xl hover:shadow-amber-900/5 bg-white ring-1 ring-slate-100">
                  <div className="flex flex-col md:flex-row h-full">

                    {/* Left: Leader Profile Section */}
                    <div className={`md:w-2/5 p-8 flex flex-col items-center justify-center border-b md:border-b-0 md:border-r border-slate-50 transition-colors duration-500 ${isSupervisor ? 'bg-slate-50/50 group-hover:bg-slate-50' : 'bg-amber-50/20 group-hover:bg-amber-50/40'}`}>
                      <div className="relative mb-6">
                        <div className={`w-28 h-28 rounded-3xl overflow-hidden shadow-lg transition-transform duration-700 group-hover:scale-105 ${isSupervisor ? 'ring-2 ring-slate-200' : 'ring-2 ring-amber-200'}`}>
                          <img
                            src={leader?.photo || "https://res.cloudinary.com/dwrqyrm84/image/upload/t_dp_logo/v1749389564/mq1jwwqt9xxehogbwlem.png"}
                            alt={leader?.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className={`absolute -bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest shadow-sm ${isSupervisor ? 'bg-slate-800 text-white' : 'bg-amber-600 text-white'}`}>
                          {isSupervisor ? 'Supervisor' : 'Archdeacon'}
                        </div>
                      </div>
                      <p className="text-center text-base font-serif font-bold text-slate-800 leading-tight">
                        {leader ? `${leader.title} ${leader.name}` : 'Position Vacant'}
                      </p>
                    </div>

                    {/* Right: Archdeaconry Details */}
                    <div className="md:w-3/5 p-10 flex flex-col">
                      <div className="flex justify-between items-start mb-6">
                        <CardTitle className="text-3xl font-serif font-bold text-slate-900 group-hover:text-amber-700 transition-colors">
                          {arch.name}
                        </CardTitle>
                        <div className="bg-slate-900 text-white h-10 w-10 rounded-2xl flex flex-col items-center justify-center shadow-lg group-hover:bg-amber-600 transition-colors">
                          <span className="text-xs font-black leading-none">{arch.parishes?.length || 0}</span>
                          <span className="text-[7px] uppercase font-black">Parishes</span>
                        </div>
                      </div>

                      <div className="flex-grow">
                        <p className="text-slate-500 text-sm leading-relaxed italic mb-8">
                          {arch.description || `Dedicated to spiritual growth and community development within the ${arch.name} jurisdiction.`}
                        </p>
                      </div>

                      <div className="flex items-center justify-between pt-6 border-t border-slate-100">
                        <div className="flex items-center gap-2 text-slate-400">
                          <Shield size={14} className={isSupervisor ? 'text-slate-300' : 'text-amber-500'} />
                          <span className="text-[10px] font-black uppercase tracking-tighter">IDS District Ref</span>
                        </div>
                        
                        <Link
                          to={`/archdeaconries/${arch.slug}`}
                          className="group/btn flex items-center gap-2 text-amber-700 font-black text-[10px] uppercase tracking-[0.2em] hover:text-slate-900 transition-all"
                        >
                          Explore Parishes 
                          <ChevronRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
                        </Link>
                      </div>
                    </div>

                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Archdeaconries;